import { getApiUrl, API_ENDPOINTS } from '../config/api';

export interface PointsBalance {
  monthlyPoints: number;
  pointsUsed: number;
  pointsRemaining: number;
  lastReset: Date;
  isUnlimited?: boolean;
  dailyUsed?: number;
  dailyLimit?: number;
}

export interface PointTransaction {
  id: string;
  userId: string;
  type: 'usage' | 'allocation' | 'admin_adjustment';
  amount: number;
  featureType?: string;
  description: string;
  metadata?: Record<string, any>;
  createdAt: string;
}

export interface PointsCostInfo {
  totalCost: number;
  canAfford: boolean;
  remainingAfter: number;
}

class PointsService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async getPointsData() {
    const response = await fetch(getApiUrl('/usage/points'), {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch points data');
    }

    return response.json();
  }

  async getTransactionHistory(limit: number = 20) {
    const response = await fetch(getApiUrl(`/usage/points/history?limit=${limit}`), {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch transaction history');
    }

    const data = await response.json();
    return data.transactions || [];
  }

  async calculateCost(featureType: string, amount: number = 1): Promise<PointsCostInfo> {
    // Point costs from backend - we'll make this dynamic later
    const POINT_COSTS: Record<string, number> = {
      'CAPTION': 7,
      'THUMBNAIL': 18,
      'TITLE_DESCRIPTION': 27,
      'VOICE_SYNTHESIS': 67,
      'MUSIC_GENERATION': 13,
      'IMAGE_ANALYSIS': 27,
      'PROMPT': 20,
      'SCRIPT': 47,
      'IMAGE_GENERATION': 18,
      'VIDEO_GENERATION': 167,
      'VIDEO_OPTIMIZATION': 7,
    };

    const costPerUnit = POINT_COSTS[featureType.toUpperCase()] || 0;
    const totalCost = costPerUnit * amount;

    // Get current balance to check affordability
    const pointsData = await this.getPointsData();
    const balance = pointsData.balance;
    const canAfford = balance && balance.pointsRemaining >= totalCost;
    const remainingAfter = balance ? Math.max(0, balance.pointsRemaining - totalCost) : 0;

    return {
      totalCost,
      canAfford,
      remainingAfter,
    };
  }
}

export const pointsService = new PointsService();
